import { useQuery } from '@apollo/client';

import type { Recommender } from '@coursera/event-pulse-types';

import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import XdpProductRecommendationsQuery from 'bundles/unified-description-page-common/queries/XdpProductRecommendations.graphql';
import type {
  XdpRec_ProductCardFragment as ProductCard,
  XdpRecommendedQuery,
  XdpRecommendedQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/XdpProductRecommendations';
import { convertProductCardS12nOrCourseToCollectionEntity } from 'bundles/unified-description-page-common/utils/dataTransformation';
import type { ProductVariantValue } from 'bundles/unified-description-page-common/utils/getIdToPrefixedStringKey';
import getIdToPrefixedStringKey from 'bundles/unified-description-page-common/utils/getIdToPrefixedStringKey';

type Props = {
  productVariant: ProductVariantValue;
  productId?: string;
  skip: boolean;
  showOnlyCplusEligibleRecs: boolean;
};

export type XdpRecommendationsQueryData = {
  eventingRecommender: Recommender | undefined;
  collectionTrackingId: string;
  recommendationCollectionEntities: CollectionEntity[];
  loading: boolean;
};

const isProductCardEntity = (product: unknown): product is ProductCard => {
  return (product as ProductCard).id !== undefined;
};

const resultsToCollectionEntities = (data: XdpRecommendedQuery | undefined): CollectionEntity[] => {
  if (!data) return [];
  const products = data.Collection?.queryCollection.entities?.elements ?? [];
  const entities: CollectionEntity[] = products
    ?.filter(isProductCardEntity)
    .map((product, index) => convertProductCardS12nOrCourseToCollectionEntity(product, index))
    .filter(Boolean);
  return entities;
};

const useXdpRecommendationsQuery = ({
  productVariant,
  productId,
  skip: skipOverride,
  showOnlyCplusEligibleRecs,
}: Props): XdpRecommendationsQueryData => {
  const { data, loading } = useQuery<XdpRecommendedQuery, XdpRecommendedQueryVariables>(
    XdpProductRecommendationsQuery,
    {
      context: { clientName: 'gatewayGql' },
      variables: {
        // we can safely add the fallback for productId here because the query does not actually run if programId is falsy
        programPrefixedStringKey: getIdToPrefixedStringKey(productId ?? '', productVariant),
        removeNonCourseraPlus: showOnlyCplusEligibleRecs,
      },
      skip: skipOverride || !productId,
    }
  );

  const recommender: Recommender | undefined = data?.Collection?.queryCollection?.recommenderConfigurationHash
    ? {
        context: data.Collection.queryCollection.context,
        hash: data.Collection.queryCollection.recommenderConfigurationHash,
      }
    : undefined;

  return {
    eventingRecommender: recommender,
    recommendationCollectionEntities: resultsToCollectionEntities(data),
    collectionTrackingId: data?.Collection?.queryCollection?.collectionTrackingId ?? '',
    loading,
  };
};

export default useXdpRecommendationsQuery;
